import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Home, Calendar, MessageSquare, Menu, Plus, X, UserPlus, FileText, Receipt } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/tech', icon: Home, label: 'Home', exact: true },
  { to: '/tech/schedule', icon: Calendar, label: 'Schedule' },
  { to: '/tech/messages', icon: MessageSquare, label: 'Messages' },
  { to: '/tech/more', icon: Menu, label: 'More' },
];

const FAB_ACTIONS = [
  { to: '/tech/new-lead', icon: UserPlus, label: 'New Lead', color: 'bg-emerald-500' },
  { to: '/tech/new-estimate', icon: FileText, label: 'New Estimate', color: 'bg-blue-500' },
  { to: '/tech/new-invoice', icon: Receipt, label: 'New Invoice', color: 'bg-amber-500' },
];

// Routes where FAB should be hidden (create/detail pages already have their own actions)
const HIDE_FAB_PATHS = ['/tech/new-lead', '/tech/new-estimate', '/tech/new-invoice', '/tech/profile', '/tech/my-vehicle'];

export default function TechLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [fabOpen, setFabOpen] = useState(false);

  const showFab = !HIDE_FAB_PATHS.some(p => location.pathname.startsWith(p))
    && !location.pathname.includes('/jobs/')
    && !location.pathname.includes('/estimates/');

  return (
    <div className="flex flex-col h-dvh bg-slate-50" data-testid="tech-layout">
      {/* ── Scrollable content area ── */}
      <main className="flex-1 overflow-y-auto pb-20">
        <Outlet />
      </main>

      {/* ── FAB Overlay ── */}
      {fabOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px]"
          onClick={() => setFabOpen(false)}
          data-testid="tech-fab-backdrop"
        />
      )}

      {/* ── FAB Actions ── */}
      {showFab && fabOpen && (
        <div className="fixed bottom-[88px] right-4 z-[70] flex flex-col-reverse items-end gap-3" data-testid="tech-fab-menu">
          {FAB_ACTIONS.map(({ to, icon: Icon, label, color }, idx) => (
            <button
              key={to}
              className="flex items-center gap-2.5 animate-in fade-in slide-in-from-bottom-2"
              style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'both' }}
              onClick={() => { setFabOpen(false); navigate(to); }}
              data-testid={`tech-fab-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="text-sm font-bold text-white bg-slate-900/80 rounded-lg px-3 py-1.5 shadow-lg">
                {label}
              </span>
              <span className={`w-11 h-11 rounded-full ${color} text-white flex items-center justify-center shadow-lg`}>
                <Icon className="w-5 h-5" />
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ── FAB Button ── */}
      {showFab && (
        <button
          onClick={() => setFabOpen(prev => !prev)}
          className={`fixed bottom-[88px] right-4 z-[70] w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-200 ${
            fabOpen
              ? 'bg-slate-800 rotate-45 scale-95'
              : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
          }`}
          data-testid="tech-fab-btn"
        >
          {fabOpen
            ? <X className="w-6 h-6 text-white" />
            : <Plus className="w-7 h-7 text-white" strokeWidth={2.5} />
          }
        </button>
      )}

      {/* ── Bottom Navigation ── */}
      <nav
        className="fixed bottom-0 inset-x-0 z-50 bg-white border-t-2 border-slate-900 safe-area-bottom"
        data-testid="tech-bottom-nav"
      >
        <div className="flex items-stretch max-w-lg mx-auto">
          {NAV_ITEMS.map(({ to, icon: Icon, label, exact }) => {
            const isActive = exact
              ? location.pathname === to
              : location.pathname.startsWith(to);
            return (
              <NavLink
                key={to}
                to={to}
                onClick={() => setFabOpen(false)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors ${
                  isActive
                    ? 'text-blue-600'
                    : 'text-slate-400 active:text-slate-600'
                }`}
                data-testid={`tech-nav-${label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 1.8} />
                <span className={`text-[10px] tracking-wide uppercase ${isActive ? 'font-bold' : 'font-medium'}`}>
                  {label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
